import { useMutation } from 'react-query';
import {
	acceptLicenseAgreement,
	changePassword,
	completePasswordReset,
	disable2FA,
	enable2FA,
	inviteUser,
	login,
	requestPasswordResetOtp,
	resendOTP,
	resetPassword,
	sendOTP,
	updatePassword,
	verifyPasswordResetOtp,
	verifyOTP,
} from '@/services/auth';
import type {
	AcceptLicenseResponse,
	ChangePasswordResponse,
	Disable2FAResponse,
	Enable2FAResponse,
	InviteUserResponse,
	LoginResponse,
	PasswordResetCompletionResponse,
	PasswordResetOtpRequestResponse,
	PasswordResetOtpVerificationResponse,
	ResetPasswordResponse,
	SendOTPResponse,
	UpdatePasswordResponse,
} from '@/types/auth';

export const useLogin = (
	onSuccess: (data: LoginResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({
			username,
			password,
		}: {
			username: string;
			password: string;
		}) => login(username, password),
		onSuccess: (data: LoginResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useUpdatePassword = (
	onSuccess: (data: UpdatePasswordResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({
			email,
			currentPassword,
		}: {
			email: string;
			currentPassword: string;
		}) => updatePassword(email, currentPassword),
		onSuccess: (data: UpdatePasswordResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useInviteUser = (
	onSuccess: (data: InviteUserResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email, fullName }: { email: string; fullName: string }) =>
			inviteUser(email, fullName),
		onSuccess: (data: InviteUserResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useResetPassword = (
	onSuccess: (data: ResetPasswordResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email }: { email: string }) => resetPassword(email),
		onSuccess: (data: ResetPasswordResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useChangePassword = (
	onSuccess: (data: ChangePasswordResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({
			password,
			accessToken,
		}: {
			password: string;
			accessToken: string;
		}) => changePassword(password, accessToken),
		onSuccess: (data: ChangePasswordResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useRequestPasswordResetOtp = (
	onSuccess: (data: PasswordResetOtpRequestResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email }: { email: string }) => requestPasswordResetOtp(email),
		onSuccess: (data: PasswordResetOtpRequestResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useVerifyPasswordResetOtp = (
	onSuccess: (data: PasswordResetOtpVerificationResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email, otp }: { email: string; otp: string }) =>
			verifyPasswordResetOtp(email, otp),
		onSuccess: (data: PasswordResetOtpVerificationResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useCompletePasswordReset = (
	onSuccess: (data: PasswordResetCompletionResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({
			email,
			password,
			resetToken,
		}: {
			email: string;
			password: string;
			resetToken: string;
		}) => completePasswordReset(email, password, resetToken),
		onSuccess: (data: PasswordResetCompletionResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useSendOTP = (
	onSuccess: (data: SendOTPResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email }: { email: string }) => sendOTP(email),
		onSuccess: (data: SendOTPResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useVerifyOTP = (
	onSuccess: (data: LoginResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ otp, email }: { otp: string; email?: string }) =>
			verifyOTP(otp, email),
		onSuccess: (data: LoginResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useResendOTP = (
	onSuccess: (data: SendOTPResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email }: { email: string }) => resendOTP(email),
		onSuccess: (data: SendOTPResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useEnable2FA = (
	onSuccess: (data: Enable2FAResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email, password }: { email: string; password: string }) => enable2FA(email, password),
		onSuccess: (data: Enable2FAResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useDisable2FA = (
	onSuccess: (data: Disable2FAResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email, password }: { email: string; password: string }) => disable2FA(email, password),
		onSuccess: (data: Disable2FAResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};

export const useAcceptLicenseAgreement = (
	onSuccess: (data: AcceptLicenseResponse) => void,
	onError: (error: unknown) => void,
) => {
	return useMutation({
		mutationFn: ({ email }: { email: string }) => acceptLicenseAgreement(email),
		onSuccess: (data: AcceptLicenseResponse) => {
			onSuccess(data);
		},
		onError: (error: unknown) => {
			onError(error);
		},
	});
};
